import type { DiagramSpec } from '../types'

/**
 * VidMax Pipeline Workflows — shows each pipeline as an independent
 * column with its own dedicated model stack (nodes duplicated per pipeline).
 */
export const vidmaxPipelines: DiagramSpec = {
  id: 'vidmax-pipelines',
  name: 'VidMax Pipeline Workflows',
  description: 'Side-by-side pipeline stacks with dedicated model clusters',
  layers: [
    { id: 'pipeline', name: 'Pipeline', color: '#f59e0b' },
    { id: 'script', name: 'Script', color: '#6366f1' },
    { id: 'voice', name: 'Voice', color: '#ec4899' },
    { id: 'video', name: 'Video Gen', color: '#10b981' },
    { id: 'output', name: 'Output', color: '#64748b' },
  ],
  nodes: [
    // ── Pipeline 1: Talking Head ──
    {
      id: 'p1-pipeline',
      label: 'Talking Head Pipeline',
      layer: 'pipeline',
      rank: 0,
      description: 'Avatar speaks to camera with script + voice',
    },
    {
      id: 'p1-script',
      label: 'Claude Sonnet',
      layer: 'script',
      rank: 1,
      description: 'Script generation',
    },
    {
      id: 'p1-voice',
      label: 'ElevenLabs',
      layer: 'voice',
      rank: 2,
      description: 'Voice synthesis & cloning',
    },
    {
      id: 'p1-video',
      label: 'Seedance',
      layer: 'video',
      rank: 3,
      description: 'Video generation from audio + image',
    },
    {
      id: 'p1-edit',
      label: 'NanoBanana',
      layer: 'video',
      rank: 4,
      description: 'Auto-edit, captions, transitions',
    },
    {
      id: 'p1-output',
      label: 'Final MP4',
      layer: 'output',
      rank: 5,
      description: '2–10 min talking head video',
      fields: { Format: 'MP4', Duration: '2–10 min' },
    },

    // ── Pipeline 2: Animation / B-Roll ──
    // Ranks aligned with Pipeline 1 (skip rank 2 — no voice step)
    {
      id: 'p2-pipeline',
      label: 'Animation Pipeline',
      layer: 'pipeline',
      rank: 0,
      description: 'Text-to-video with AI-generated visuals',
    },
    {
      id: 'p2-script',
      label: 'Claude Sonnet',
      layer: 'script',
      rank: 1,
      description: 'Script + scene prompts',
    },
    {
      id: 'p2-video',
      label: 'Veo 3',
      layer: 'video',
      rank: 3,
      description: 'High-quality text-to-video generation',
    },
    {
      id: 'p2-edit',
      label: 'NanoBanana',
      layer: 'video',
      rank: 4,
      description: 'Auto-edit, captions, transitions',
    },
    {
      id: 'p2-output',
      label: 'Final MP4',
      layer: 'output',
      rank: 5,
      description: '30s–3 min animated video',
      fields: { Format: 'MP4', Duration: '30s–3 min' },
    },
  ],
  edges: [
    // Pipeline 1 flow
    { id: 'p1-e1', source: 'p1-pipeline', target: 'p1-script' },
    { id: 'p1-e2', source: 'p1-script', target: 'p1-voice' },
    { id: 'p1-e3', source: 'p1-voice', target: 'p1-video' },
    { id: 'p1-e4', source: 'p1-video', target: 'p1-edit' },
    { id: 'p1-e5', source: 'p1-edit', target: 'p1-output' },

    // Pipeline 2 flow
    { id: 'p2-e1', source: 'p2-pipeline', target: 'p2-script' },
    { id: 'p2-e2', source: 'p2-script', target: 'p2-video' },
    { id: 'p2-e3', source: 'p2-video', target: 'p2-edit' },
    { id: 'p2-e4', source: 'p2-edit', target: 'p2-output' },
  ],
  views: [
    { id: 'full', name: 'All Pipelines', layout: 'TB' },
    {
      id: 'p1-only',
      name: 'Talking Head Only',
      layers: ['pipeline', 'script', 'voice', 'video', 'output'],
      layout: 'TB',
    },
  ],
  defaultView: 'full',
  preferences: {
    nodeWidth: 220,
    rankSpacing: 120,
    nodeGap: 80,
    edgeStyle: 'smoothstep',
    showLayerBands: false,
  },
}
